<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'BtnSyncResource',
    inheritAttrs: false,
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'success']);

  const props = defineProps({
    resource: {
      type: Object,
      required: true,
    },
    headerMessage: {
      type: String,
      required: false,
    },
    confirmMessage: {
      type: String,
      required: false,
    },
    btnOK: {
      type: String,
      required: false,
    },
    size: {
      type: String,
      required: false,
    },
    target: {
      type: String,
      required: false,
      default: 'irent',
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const messages = ref({
    text: $fn.value.pgettext('BtnDeleteResource', 'Delete'),
    header: $fn.value.pgettext(
      'BtnSyncResource',
      'IntegroRent synchronization'
    ),
    confirmText: $fn.value.pgettext(
      'BtnSyncResource',
      'Write data back to the IntegroRent system'
    ),
    btnOk: $fn.value.pgettext('BtnSyncResource', 'Synchronization'),
    btnCancel: $fn.value.pgettext('BtnSyncResource', 'Cancel'),
  });
  const ux = ref([]);

  const $data = computed(() => ({
    messages: messages.value,
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'messages' }, { name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { messages, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const synced = computed(() => {
    return !!(props.resource.sync_status || props.resource.external_key);
  });
  const tooltipMessage = computed(() => {
    return synced.value
      ? $fn.value.pgettext('BtnSyncResource', 'Synchronized') +
          ` [${props.resource.external_key}]`
      : $fn.value.pgettext('BtnSyncResource', 'Synchronization');
  });

  watch(
    () => props.resource,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('resource');
    }
  );
  watch(
    () => props.headerMessage,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('headerMessage');
    }
  );
  watch(
    () => props.confirmMessage,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('confirmMessage');
    }
  );
  watch(
    () => props.btnOK,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('btnOK');
    }
  );
  watch(
    () => props.size,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('size');
    }
  );
  watch(
    () => props.target,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('target');
    }
  );

  const act_7315092382398562_emit_h8722en53_getActionArgs = async ($event) => {
    return {
      event: 'success',
    };
  };
  const act_7315092382398562_request_j1e0o18q5_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'sync_' + props.resource.object,
      data: { id: props.resource.id, target: props.target || 'irent' },
    };
  };
  const act_7315092382398562_request_j1e0o18q5_getConfirm = async ($event) => {
    return {
      header: props.headerMessage || messages.value.header,
      message: props.confirmMessage || messages.value.confirmText,
      btnOk: props.btnOK || messages.value.btnOk,
      btnCancel: messages.value.btnCancel,
      severity: 'warning',
    };
  };

  const act_7315092382398562_emit_h8722en53 = new actionTypes.EmitAction({
    actionArgs: act_7315092382398562_emit_h8722en53_getActionArgs,
  });
  const inst_evh_7315092382398562_success_gp6ei2z5j =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_emit_h8722en53,
      event: 'success',
      displayName: 'emit',
    });
  const act_7315092382398562_request_j1e0o18q5 = new actionTypes.RequestAction({
    actionArgs: act_7315092382398562_request_j1e0o18q5_getActionArgs,
    confirm: act_7315092382398562_request_j1e0o18q5_getConfirm,
    displayName: 'sync',
    events: [inst_evh_7315092382398562_success_gp6ei2z5j],
  });
  const inst_evh_7315092382398562_clickPreventStop_9uyt6ws6k =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_request_j1e0o18q5,
      event: 'click.prevent.stop',
      displayName: 'sync',
    });
  const inst_evh_7315092382398562_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7315092382398562_clickPreventStop_9uyt6ws6k],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_success_gp6ei2z5j,
    inst_evh_7315092382398562_clickPreventStop_9uyt6ws6k,
    sync: inst_evh_7315092382398562_clickPreventStop_9uyt6ws6k,
    inst_evh_7315092382398562_clickPreventStop,
  };

  const evh_7315092382398562_success_gp6ei2z5j = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_success_gp6ei2z5j.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_clickPreventStop_9uyt6ws6k = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7315092382398562_clickPreventStop_9uyt6ws6k.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpButton
    :icon="
      synced
        ? 'fluent:cloud-sync-complete-16-filled'
        : 'material-symbols:cloud-sync-outline-rounded'
    "
    :class="synced ? 'bg-green-200' : 'bg-yellow-200'"
    :tooltip="tooltipMessage"
    :spinning="actions.sync.isRunning"
    variant="light"
    :disabled="synced"
    :size="size"
    @click.prevent.stop="evh_7315092382398562_clickPreventStop($event, {})"
  >
  </ItpButton>
</template>
